import { useGetFeatures } from 'common/queries/features';
import { mergeFeatureGates } from 'utils/features';
import { useCallback, useMemo } from 'react';
import { useKpisList } from 'common/hooks/kpis';
import { BRAND_PERCEPTION_KPI, MARKET_FUNNEL_KPIS, MARKET_SIZE_KPI } from 'settings/constants';
import { FEATURES } from 'common/state/constants';
import { createKpiMap } from 'utils/kpi';

interface Features {
  features: Array<string>;
  isLoading: boolean;
}

export const useGetAllFeaturesGates = (studyId?: string): Features => {
  const { data, isLoading } = useGetFeatures();

  return useMemo(
    () => ({
      features: data ? mergeFeatureGates(data, studyId) : [],
      isLoading,
    }),
    [data, studyId, isLoading],
  );
};

export const useIsMarketSizeChartsEnabled = (): ((studyId: string) => boolean) => {
  const getKpiList = useKpisList();

  const isMarketSizeChartsEnabled = useCallback(
    (studyId: string) => {
      const marketSizeKpi = getKpiList(studyId)?.find(({ kpi_identifier }) => kpi_identifier === MARKET_SIZE_KPI);
      return !!marketSizeKpi;
    },
    [getKpiList],
  );

  return isMarketSizeChartsEnabled;
};

export const useIsChartAverage2Enabled = (studyId?: string): boolean => {
  const { features } = useGetAllFeaturesGates(studyId);

  return useMemo(() => {
    return features.includes(FEATURES.CHART_AVERAGE_2);
  }, [features]);
};

export const useIsBrandPerceptionChartsEnabled = (): ((studyId: string) => boolean) => {
  const getKpiList = useKpisList();

  const isBrandPerceptionChartsEnabled = useCallback(
    (studyId: string) => {
      const brandPerceptionKpi = getKpiList(studyId).find(({ kpi_identifier }) => kpi_identifier === BRAND_PERCEPTION_KPI);
      return !!brandPerceptionKpi;
    },
    [getKpiList],
  );

  return isBrandPerceptionChartsEnabled;
};

export const useIsMarketFunnelChartsEnabled = (): ((studyId: string) => boolean) => {
  const getKpiList = useKpisList();

  const isMarketFunnelChartsEnabled = useCallback(
    (studyId: string) => {
      const marketFunnelKpisExist = MARKET_FUNNEL_KPIS.every(kpi => getKpiList(studyId)?.some(x => x.kpi_identifier === kpi));
      return marketFunnelKpisExist;
    },
    [getKpiList],
  );

  return isMarketFunnelChartsEnabled;
};

export const useIsCompetitorsEnabled = (studyId?: string): boolean => {
  const { features } = useGetAllFeaturesGates(studyId);

  return useMemo(() => {
    return features.includes(FEATURES.COMPETITORS);
  }, [features]);
};

export const useHidePopulationNumbersEnabled = (studyId: string): boolean => {
  const { features } = useGetAllFeaturesGates(studyId);

  return useMemo(() => {
    return features.includes(FEATURES.HIDE_POPULATION_NUMBERS);
  }, [features]);
};

export const useIsPopulationNumbersEnabled = (studyId?: string): boolean => {
  const { features } = useGetAllFeaturesGates(studyId);

  return useMemo(() => {
    return features.includes(FEATURES.ENABLE_POPULATION_NUMBERS);
  }, [features]);
};

export const useIsMatrixChartsEnabled = (): ((studyId: string) => boolean) => {
  const getKpiList = useKpisList();
  const { features } = useGetAllFeaturesGates();

  const isMatrixChartsEnabled = useCallback(
    (studyId: string) => {
      const kpiMap = createKpiMap(getKpiList(studyId));
      const kpiIds = Object.keys(kpiMap);

      /* Check if there are more than 1 kpi and at least one kpi does not start with "brand-perception", 
      because we merge multiple brand-perception into single net sentiment */
      const hasNonBrandConsiderationKey = kpiIds.length > 1 && kpiIds.some(key => !key.startsWith(BRAND_PERCEPTION_KPI));

      return features.includes(FEATURES.MATRIX_CHART) && hasNonBrandConsiderationKey;
    },
    [getKpiList, features],
  );

  return isMatrixChartsEnabled;
};

export const useIsBrandFunnelChartsEnabled = (): ((studyId: string) => boolean) => {
  const { data } = useGetFeatures();

  return useCallback((studyId: string) => !!data && mergeFeatureGates(data, studyId).includes(FEATURES.BRAND_FUNNEL_CHART), [data]);
};

export const useIsChartFiltersOnDashboardEnabled = (studyId?: string): boolean => {
  const { features } = useGetAllFeaturesGates(studyId);

  return useMemo(() => {
    return features.includes(FEATURES.SHOW_CHART_FILTERS_ON_DASHBOARD);
  }, [features]);
};

export const useAiChatBotEnabled = (): Record<'isEnabled' | 'isLoading', boolean> => {
  const { features, isLoading } = useGetAllFeaturesGates();

  return useMemo(() => ({ isEnabled: features.includes(FEATURES.AI_CHAT_BOT), isLoading }), [features, isLoading]);
};

/** Customer-level gate for the customer data export (download API tokens and webhooks). */
export const useIsCustomerDataExportEnabled = (): boolean => {
  const { features } = useGetAllFeaturesGates();

  return useMemo(() => features.includes(FEATURES.CUSTOMER_DATA_EXPORT), [features]);
};
